import * as THREE from 'three';
import type { SketchFace, SketchVec3 } from './types';

/**
 * mesh → analytic solid rebuild ("hermeticity guard" companion, REPAIR-FIRST).
 *
 * After a CSG step the shell exists only as a triangle mesh. Boolean kernels
 * leak T-junctions and open seams whenever operands touch EXACTLY — a flush
 * Push/Pull start, a through-cut exiting coplanar with the far face, two
 * solids fused along a shared wall — and three-bvh-csg's tolerances are
 * tight (1e-10). Rejecting every non-watertight mesh would therefore reject
 * exactly the operations SketchUp models every day, so this module repairs
 * before it ever rejects:
 *   1. weld vertices by position (with a 27-cell neighbor probe so border-
 *      straddling kernel outputs still merge),
 *   2. T-JUNCTION PASS — split triangles at welded vertices sitting on
 *      their boundary edges,
 *   3. HOLE CAPPING — chain leftover boundary edges into directed loops and
 *      close each with a triangulated patch (a missing coplanar patch
 *      reappears exactly; a cut silhouette closes along its own contour).
 *      The chaining is pinch-safe: loops sharing a single welded vertex are
 *      split apart instead of discarding each other,
 *   4. only meshes with un-cappable holes are rejected (vanishingly rare).
 * Analytic faces are then reconstructed WITHOUT 2D polygon booleans:
 *   bucket triangles by quantized plane (normal + offset), split each bucket
 *   into connected components, chain each component's boundary edges into
 *   loops — longest loop is the outer contour, rest are holes. Duplicate or
 *   seam-glued patches collapse naturally in the analytic rebuild.
 */

const PLANE_EPS = 1e-4;
const POINT_EPS = 1e-5;
/** Weld radius: within it, matching points always land in adjacent cells. */
const WELD_EPS = POINT_EPS;
/** Max repair passes over the mesh (T-junction splitting converges fast). */
const REPAIR_PASSES = 4;
/** Collinearity slack for T-junction detection (relative to edge length). */
const COLLINEAR_EPS = 1e-4;

const cellOf = (v: THREE.Vector3): [number, number, number] => [
    Math.round(v.x / POINT_EPS),
    Math.round(v.y / POINT_EPS),
    Math.round(v.z / POINT_EPS),
];

const cellId = (cx: number, cy: number, cz: number): string => `${cx}|${cy}|${cz}`;

const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];

/**
 * Welded triangle soup: vertices are deduplicated by quantized position, so
 * edge-usage counts across coincident geometry are exact.
 */
interface WeldMesh {
    vertices: THREE.Vector3[];
    tris: Array<[number, number, number]>;
}

/** Read position triplets of a (possibly interleaved) geometry, welded. */
function readWelded(geometry: THREE.BufferGeometry): WeldMesh {
    const pos = geometry.getAttribute('position');
    const mesh: WeldMesh = { vertices: [], tris: [] };
    if (!pos) return mesh;
    const index = new Map<string, number>();
    const pa = new THREE.Vector3();
    const pb = new THREE.Vector3();
    const pc = new THREE.Vector3();
    const corner = (v: THREE.Vector3): number => {
        const [cx, cy, cz] = cellOf(v);
        // Probe the 27 neighboring cells: two kernel outputs describing the
        // SAME point can straddle a quantization cell border (a few ulps
        // apart) — a plain same-cell lookup would leave them un-welded and
        // tear an open seam straight through the shell.
        for (let dx = -1; dx <= 1; dx++) {
            for (let dy = -1; dy <= 1; dy++) {
                for (let dz = -1; dz <= 1; dz++) {
                    const known = index.get(cellId(cx + dx, cy + dy, cz + dz));
                    if (known !== undefined && mesh.vertices[known].distanceTo(v) <= WELD_EPS) {
                        return known;
                    }
                }
            }
        }
        const id = mesh.vertices.length;
        mesh.vertices.push(v.clone());
        index.set(cellId(cx, cy, cz), id);
        return id;
    };
    for (let i = 0; i < pos.count; i += 3) {
        pa.fromBufferAttribute(pos, i);
        pb.fromBufferAttribute(pos, i + 1);
        pc.fromBufferAttribute(pos, i + 2);
        const ab = new THREE.Vector3().subVectors(pb, pa);
        const ac = new THREE.Vector3().subVectors(pc, pa);
        const area2 = new THREE.Vector3().crossVectors(ab, ac).length();
        if (area2 < 1e-14) continue; // degenerate sliver
        mesh.tris.push([corner(pa), corner(pb), corner(pc)]);
    }
    return mesh;
}

/** Undirected edge id from welded vertex indices (order-independent). */
const edgeKey = (i: number, j: number): string => (i < j ? `${i}~${j}` : `${j}~${i}`);

/** Canonical edge → triangle indices (rebuilt from scratch on demand). */
function edgeTable(tris: Array<[number, number, number]>): Map<string, number[]> {
    const table = new Map<string, number[]>();
    tris.forEach((tri, t) => {
        for (let e = 0; e < 3; e++) {
            const key = edgeKey(tri[e], tri[(e + 1) % 3]);
            const list = table.get(key);
            if (list) list.push(t);
            else table.set(key, [t]);
        }
    });
    return table;
}

/**
 * Split triangles at welded vertices that sit ON one of their edges without
 * being its endpoints (the boolean kernel's classic seam leak): each split
 * turns the T-junction's dangling sub-edges into shared ones. Bounded
 * passes; the edge table is re-derived after every pass.
 */
function repairTJunctions(mesh: WeldMesh): void {
    const v = mesh.vertices;
    const ab = new THREE.Vector3();
    const ap = new THREE.Vector3();
    const cr = new THREE.Vector3();
    const rotateToEdge = (
        tri: [number, number, number],
        p: number,
        q: number
    ): [number, number, number] | null => {
        for (let s = 0; s < 3; s++) {
            const a = tri[s];
            const b = tri[(s + 1) % 3];
            const c = tri[(s + 2) % 3];
            if (a === p && b === q) return [a, b, c];
        }
        return null;
    };
    for (let pass = 0; pass < REPAIR_PASSES; pass++) {
        const table = edgeTable(mesh.tris);
        const splits: Array<{ tri: number; p: number; q: number; mid: number }> = [];
        table.forEach((owners, key) => {
            if (owners.length !== 1) return;
            const [i, j] = key.split('~').map(Number);
            const a = v[i];
            const b = v[j];
            const len2 = ab.subVectors(b, a).lengthSq();
            if (len2 < 1e-18) return;
            for (let k = 0; k < v.length; k++) {
                if (k === i || k === j) continue;
                ap.subVectors(v[k], a);
                // Strictly between the endpoints and on the segment line.
                const t = ap.dot(ab) / len2;
                if (t <= COLLINEAR_EPS || t >= 1 - COLLINEAR_EPS) continue;
                if (cr.crossVectors(ap, ab).lengthSq() > COLLINEAR_EPS * COLLINEAR_EPS * len2) continue;
                splits.push({ tri: owners[0], p: i, q: j, mid: k });
                break;
            }
        });
        if (splits.length === 0) return;
        for (const { tri, p, q, mid } of splits) {
            const rotated =
                rotateToEdge(mesh.tris[tri], p, q) ?? rotateToEdge(mesh.tris[tri], q, p);
            if (!rotated) continue; // already split this pass — re-tabled next pass
            const [a, b, c] = rotated;
            mesh.tris[tri] = [a, mid, c];
            mesh.tris.push([mid, b, c]);
        }
    }
}

interface Bucket {
    normal: THREE.Vector3;
    d: number;
    tris: number[];
}

/** Bucket triangles by quantized plane equation. */
function bucketByPlane(mesh: WeldMesh): Bucket[] {
    const buckets = new Map<string, Bucket>();
    const ab = new THREE.Vector3();
    const ac = new THREE.Vector3();
    const normal = new THREE.Vector3();
    mesh.tris.forEach((tri, t) => {
        const a = mesh.vertices[tri[0]];
        ab.subVectors(mesh.vertices[tri[1]], a);
        ac.subVectors(mesh.vertices[tri[2]], a);
        normal.crossVectors(ab, ac).normalize();
        const d = -normal.dot(a);
        const key =
            `${Math.round(normal.x * 1000)}|${Math.round(normal.y * 1000)}|${Math.round(normal.z * 1000)}|${Math.round(d / PLANE_EPS)}`;
        let bucket = buckets.get(key);
        if (!bucket) {
            bucket = { normal: normal.clone(), d, tris: [] };
            buckets.set(key, bucket);
        }
        bucket.tris.push(t);
    });
    return Array.from(buckets.values());
}

/** Connected components of a bucket's triangles via shared edges. */
function components(mesh: WeldMesh, tris: number[]): number[][] {
    const edgeMap = new Map<string, number[]>();
    for (const t of tris) {
        const tri = mesh.tris[t];
        for (let e = 0; e < 3; e++) {
            const key = edgeKey(tri[e], tri[(e + 1) % 3]);
            const list = edgeMap.get(key);
            if (list) list.push(t);
            else edgeMap.set(key, [t]);
        }
    }
    const visited = new Set<number>();
    const comps: number[][] = [];
    for (const start of tris) {
        if (visited.has(start)) continue;
        const comp: number[] = [];
        const stack = [start];
        visited.add(start);
        while (stack.length > 0) {
            const t = stack.pop()!;
            comp.push(t);
            const tri = mesh.tris[t];
            for (let e = 0; e < 3; e++) {
                const key = edgeKey(tri[e], tri[(e + 1) % 3]);
                for (const j of edgeMap.get(key) ?? []) {
                    if (!visited.has(j)) {
                        visited.add(j);
                        stack.push(j);
                    }
                }
            }
        }
        comps.push(comp);
    }
    return comps;
}

/**
 * Split a closed vertex walk at repeated vertices into simple loops. A
 * pinched boundary — two holes or a hole and a seam touching at ONE welded
 * vertex — makes any walk pass through that vertex twice; slicing there
 * recovers both lobes as individually cappable loops. Loops stay open (the
 * closing vertex is not duplicated).
 */
function splitAtRepeats(loop: number[]): number[][] {
    const findRepeat = (l: number[]): [number, number] | null => {
        for (let i = 0; i < l.length; i++) {
            for (let j = i + 1; j < l.length; j++) {
                if (l[i] === l[j]) return [i, j];
            }
        }
        return null;
    };
    const out: number[][] = [];
    const queue = [loop];
    while (queue.length > 0) {
        const cur = queue.pop()!;
        const hit = findRepeat(cur);
        if (!hit) {
            if (cur.length >= 3) out.push(cur);
            continue;
        }
        const [p, q] = hit;
        queue.push(cur.slice(p, q)); // lobe between the two visits
        queue.push([...cur.slice(0, p), ...cur.slice(q)]); // the rest of the walk
    }
    return out;
}

/**
 * Chain undirected boundary edges into closed vertex loops. The walk keeps a
 * MULTI-adjacency (every edge in both endpoint lists) and consumes edges by
 * id, so boundaries that pinch through a shared vertex still resolve: at an
 * even-degree vertex there is always an unused exit until the walk returns
 * to its start. Open chains (nothing to exit through) are dropped.
 */
function chainUndirected(
    boundary: Array<[number, number]>
): number[][] {
    const adjacency = new Map<number, Array<{ to: number; id: number }>>();
    boundary.forEach(([i, j], id) => {
        if (!adjacency.has(i)) adjacency.set(i, []);
        if (!adjacency.has(j)) adjacency.set(j, []);
        adjacency.get(i)!.push({ to: j, id });
        adjacency.get(j)!.push({ to: i, id });
    });
    const consumed = new Set<number>();
    const loops: number[][] = [];
    for (let id = 0; id < boundary.length; id++) {
        if (consumed.has(id)) continue;
        const [i, j] = boundary[id];
        consumed.add(id);
        const loop: number[] = [i, j];
        let cur = j;
        let closed = false;
        for (;;) {
            if (cur === i) {
                closed = true;
                break;
            }
            const next = (adjacency.get(cur) ?? []).find((e) => !consumed.has(e.id));
            if (next === undefined) break;
            consumed.add(next.id);
            if (next.to === i) {
                closed = true;
                break;
            }
            loop.push(next.to);
            cur = next.to;
        }
        if (closed && loop.length >= 3) loops.push(...splitAtRepeats(loop));
    }
    return loops;
}

/**
 * Close every boundary loop (edges used exactly once) with a triangulated
 * patch. The loop direction comes from the complementary half-edge of the
 * owning triangle (the patch must wind opposite to close the orientation),
 * so caps inherit the shell's outward winding. Returns the number of edges
 * still open afterwards — 0 means fully closed.
 */
function capBoundaryLoops(mesh: WeldMesh): number {
    const table = edgeTable(mesh.tris);
    // Directed half-edges: owner triangle winds a→b, so the closing patch
    // winds the complementary b→a (recorded as an entry `a` in out[b]). A
    // MULTI-map is essential: at a pinch vertex two boundary loops share ONE
    // welded point (a seam loop landing on an existing surface corner), and
    // a single-valued map would glue the walks together, discard both loops
    // and reject the whole shell. In/out half-edge counts balance at every
    // boundary vertex of an orientable mesh, so a greedy walk always
    // returns to its start.
    const out = new Map<number, number[]>();
    table.forEach((owners, key) => {
        if (owners.length !== 1) return;
        const [i, j] = key.split('~').map(Number);
        const tri = mesh.tris[owners[0]];
        for (let e = 0; e < 3; e++) {
            const a = tri[e];
            const b = tri[(e + 1) % 3];
            if (edgeKey(a, b) === key) {
                const list = out.get(b);
                if (list) list.push(a);
                else out.set(b, [a]);
                break;
            }
        }
    });
    if (out.size === 0) return 0;

    const loops: number[][] = [];
    for (const start of Array.from(out.keys())) {
        const starts = out.get(start);
        while (starts && starts.length > 0) {
            const loop: number[] = [start];
            let cur = start;
            let closed = false;
            for (;;) {
                const list = out.get(cur);
                const nxt = list && list.length > 0 ? list.pop() : undefined;
                if (nxt === undefined) break; // unbalanced debris — un-cappable
                if (nxt === start) {
                    closed = true;
                    break;
                }
                loop.push(nxt);
                cur = nxt;
            }
            if (closed) loops.push(...splitAtRepeats(loop));
        }
    }

    const normal = new THREE.Vector3();
    const pa = new THREE.Vector3();
    const pb = new THREE.Vector3();
    const patches: Array<[number, number, number]> = [];
    for (const loop of loops) {
        // Newell normal over the directed loop: also the patch orientation.
        normal.set(0, 0, 0);
        for (let s = 0; s < loop.length; s++) {
            pa.copy(mesh.vertices[loop[s]]);
            pb.copy(mesh.vertices[loop[(s + 1) % loop.length]]);
            normal.x += (pa.y - pb.y) * (pa.z + pb.z);
            normal.y += (pa.z - pb.z) * (pa.x + pb.x);
            normal.z += (pa.x - pb.x) * (pa.y + pb.y);
        }
        if (normal.lengthSq() < 1e-16) continue; // degenerate loop
        normal.normalize();
        const origin = mesh.vertices[loop[0]];
        const ref = Math.abs(normal.x) < 0.9
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);
        const u = new THREE.Vector3().crossVectors(ref, normal).normalize();
        const w = new THREE.Vector3().crossVectors(normal, u).normalize();
        const project = (idx: number): THREE.Vector2 => {
            const d = new THREE.Vector3().subVectors(mesh.vertices[idx], origin);
            return new THREE.Vector2(d.dot(u), d.dot(w));
        };
        let faces: number[][] = [];
        try {
            faces = THREE.ShapeUtils.triangulateShape(loop.map(project), []);
        } catch {
            faces = [];
        }
        if (faces.length === 0) {
            // Earcut refused the projection (degenerate/self-touching
            // outline): a fan still closes the hole — the analytic rebuild
            // re-derives contours from the triangle soup, overlap and all.
            for (let s = 1; s < loop.length - 1; s++) faces.push([0, s, s + 1]);
        }
        for (const tri of faces) {
            patches.push([loop[tri[0]], loop[tri[1]], loop[tri[2]]]);
        }
    }
    mesh.tris.push(...patches);

    let open = 0;
    edgeTable(mesh.tris).forEach((owners) => {
        if (owners.length === 1) open++;
    });
    return open;
}

/** Boundary loops of ONE bucket component (in-bucket edge use counts). */
function componentLoops(mesh: WeldMesh, tris: number[]): number[][] {
    const useCount = new Map<string, number>();
    for (const t of tris) {
        const tri = mesh.tris[t];
        for (let e = 0; e < 3; e++) {
            const key = edgeKey(tri[e], tri[(e + 1) % 3]);
            useCount.set(key, (useCount.get(key) ?? 0) + 1);
        }
    }
    const boundary: Array<[number, number]> = [];
    useCount.forEach((count, key) => {
        if (count !== 1) return;
        const [i, j] = key.split('~').map(Number);
        boundary.push([i, j]);
    });
    return chainUndirected(boundary);
}

export interface RebuildResult {
    ok: boolean;
    faces: SketchFace[];
    /** Human-readable guard failure (dry shell, unclosed loops...). */
    reason?: string;
}

/**
 * Rebuild analytic faces from a CSG result mesh. REPAIR-FIRST: T-junction
 * splits and hole capping run before the only remaining gate — boundary
 * edges that survive capping (a self-intersecting hole) still reject, so a
 * committed shell is always closed.
 */
export function meshToFaces(geometry: THREE.BufferGeometry): RebuildResult {
    const mesh = readWelded(geometry);
    if (mesh.tris.length === 0) return { ok: false, faces: [], reason: 'Empty mesh.' };

    // REPAIR PASS: a boolean seam must not kill the operation — SketchUp
    // models straight through these. Split T-junctions, cap boundary loops.
    repairTJunctions(mesh);
    const openEdges = capBoundaryLoops(mesh);
    if (openEdges > 0) {
        return {
            ok: false,
            faces: [],
            reason: `Unrepairable shell (${openEdges} open edges). Operation rejected.`,
        };
    }

    const faces: SketchFace[] = [];
    let faceIndex = 0;
    const bu = new THREE.Vector3();
    const bv = new THREE.Vector3();
    const bd = new THREE.Vector3();
    const prev = new THREE.Vector2();
    const cur = new THREE.Vector2();
    for (const bucket of bucketByPlane(mesh)) {
        // Projection basis of the bucket plane: loop areas and windings are
        // measured in it.
        const n = bucket.normal;
        const ref = Math.abs(n.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
        bu.crossVectors(ref, n).normalize();
        bv.crossVectors(n, bu).normalize();
        const project = (idx: number, out: THREE.Vector2): THREE.Vector2 => {
            bd.subVectors(mesh.vertices[idx], mesh.vertices[0]);
            return out.set(bd.dot(bu), bd.dot(bv));
        };
        for (const comp of components(mesh, bucket.tris)) {
            const loops = componentLoops(mesh, comp);
            if (loops.length === 0) continue;
            // Signed planar area of each loop (shoelace in the projection).
            const measure = (loop: number[]): number => {
                project(loop[loop.length - 1], prev);
                let area = 0;
                for (let i = 0; i < loop.length; i++) {
                    project(loop[i], cur);
                    area += prev.x * cur.y - cur.x * prev.y;
                    prev.copy(cur);
                }
                return area / 2;
            };
            const measured = loops.map((loop) => ({ loop, area: measure(loop) }));
            // Largest |area| is the OUTER contour. Vertex counts mean nothing
            // here — a boolean subdivides edges, so a small rectangular hole
            // can carry far more points than the true outer boundary, and
            // picking by count swaps the roles (the rebuilt face triangulates
            // its boundary as a hole and the shell shows a void).
            measured.sort((x, y) => Math.abs(y.area) - Math.abs(x.area));
            const outerEntry = measured[0];
            if (outerEntry.area < 0) outerEntry.loop.reverse(); // outer CCW from the normal side
            const holes = measured.slice(1).map((entry) => {
                if (entry.area > 0) entry.loop.reverse(); // holes CW
                return entry.loop.map((idx) => asTuple(mesh.vertices[idx]));
            });
            const outerTuples = outerEntry.loop.map((idx) => asTuple(mesh.vertices[idx]));
            faces.push({
                id: `rebuilt-${faceIndex++}`,
                origin: outerTuples[0],
                normal: asTuple(bucket.normal),
                outer: outerTuples,
                holes,
            });
        }
    }
    if (faces.length === 0) return { ok: false, faces: [], reason: 'No faces recovered.' };
    return { ok: true, faces };
}
